import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// ---------------------------------------------------------------------------
// UserPermissionMatrix — mirrors backend/src/lib/permissions/matrix.ts, the
// single source of truth for the RBAC matrix shape (CLE-215). Keep this type in
// sync with that file: `GET /api/me/permissions` returns a value of exactly this
// shape and the role-template / per-user matrices use it verbatim.
// ---------------------------------------------------------------------------

export type ProjectScope = 'all' | 'assigned' | 'specific';
export type TaskScope = 'all' | 'assigned' | 'project_scoped';
export type SummaryScope = 'self' | 'all';
/** Meetings visibility: every meeting, or only the ones the user took part in. */
export type MeetingScope = 'all' | 'involved';
export type ModelAccess = 'haiku' | 'sonnet' | 'both';

export interface UserPermissionMatrix {
  projects: {
    canView: boolean;
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
    scope: ProjectScope;
    specificProjectIds?: string[];
    // Visibility of the project "Prezentare generală" (overview) tab (CLE-431).
    canViewOverview: boolean;
    canViewFinancials: boolean;
    canEditFinancials: boolean;
    canViewTeam: boolean;
    canEditTeam: boolean;
    canViewResources: boolean;
    canEditResources: boolean;
    canViewMeetingNotes: boolean;
    canEditMeetingNotes: boolean;
    // Credentials vault — three separate levels: see the tab, reveal a secret
    // value, and create/edit/delete entries.
    canViewVault: boolean;
    canRevealVault: boolean;
    canEditVault: boolean;
  };
  tasks: {
    canView: boolean;
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canBulkEdit: boolean;
    canViewReports: boolean;
    scope: TaskScope;
  };
  leads: {
    canView: boolean;
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canConvert: boolean;
    canViewActions: boolean;
    canEditActions: boolean;
    canConfigurePipeline: boolean;
  };
  clients: {
    canView: boolean;
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canViewContacts: boolean;
    canEditContacts: boolean;
    canViewResources: boolean;
    canEditResources: boolean;
    canViewMeetingNotes: boolean;
    canEditMeetingNotes: boolean;
  };
  meetings: {
    canView: boolean;
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
    scope: MeetingScope;
    canViewNotes: boolean;
    canEditNotes: boolean;
    canManageContacts: boolean;
  };
  videoMeetings: {
    canView: boolean;
    canCreate: boolean;
    canManage: boolean;
  };
  socialPosts: {
    canView: boolean;
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canManageShareLinks: boolean;
  };
  suppliers: {
    canView: boolean;
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
  };
  /** Actori (modul la cerere): actors used in shoots + the conflict check. */
  actors: {
    canView: boolean;
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
  };
  /** WhatsApp (modul la cerere): the client conversations; configure = QR + group links. */
  whatsapp: {
    canView: boolean;
    canConfigure: boolean;
  };
  subscriptions: {
    canView: boolean;
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
  };
  financials: {
    canView: boolean;
    canViewPnL: boolean;
    canViewInvoices: boolean;
    canCreateInvoices: boolean;
    canEditInvoices: boolean;
    canDeleteInvoices: boolean;
    canViewSmartBill: boolean;
    canSyncSmartBill: boolean;
    canAssociateSmartBill: boolean;
    canViewSpv: boolean;
    canSyncSpv: boolean;
    canIssueInvoices: boolean;
    canApproveInvoices: boolean;
    canViewBankStatements: boolean;
    canUploadBankStatements: boolean;
    canReconcile: boolean;
    canExportReports: boolean;
    canSendReminders: boolean;
    canManageScheduledPayments: boolean;
  };
  people: {
    canView: boolean;
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canViewCapacity: boolean;
    canEditAllocations: boolean;
    canViewRates: boolean;
    canManageRoles: boolean;
  };
  dailySummary: {
    canView: boolean;
    scope: SummaryScope;
    canExportPdf: boolean;
    canSendEmail: boolean;
  };
  weeklySummary: {
    canView: boolean;
    scope: SummaryScope;
    canExport: boolean;
  };
  aiAssistant: {
    canAccess: boolean;
    canCreateTasks: boolean;
    canCreateLeads: boolean;
    canIngestDocuments: boolean;
    canQueryFinancials: boolean;
    canWriteKb: boolean;
    modelAccess: ModelAccess;
  };
  settings: {
    canAccessSettings: boolean;
    canManageCompanies: boolean;
    canEditIntegrations: boolean;
    canEditExpenseCategories: boolean;
    canEditLeadPipelines: boolean;
    canEditDepartments: boolean;
    canEditServices: boolean;
    canEditTaskStatuses: boolean;
    canEditTaskTemplates: boolean;
    canEditEmailTemplates: boolean;
    canManageUsers: boolean;
    canManageRoles: boolean;
  };
  // Concedii. No `canView` on purpose: filing and listing your OWN leave is
  // auth-only, so these flags only ever govern other people's requests.
  // Mirrors backend/src/lib/permissions/matrix.ts.
  leave: {
    canViewAll: boolean;
    canApprove: boolean;
    canManage: boolean;
  };

  dashboard: {
    canView: boolean;
    canViewFinancialCards: boolean;
    canViewTeamAllocation: boolean;
    canViewActivityFeed: boolean;
  };
}

export type PermissionModule = keyof UserPermissionMatrix;
export type PermissionAction = string;

// ---------------------------------------------------------------------------
// Canonical empty (deny-by-default) matrix. Mirrors the backend
// `emptyAccessMatrix()` so the frontend can render with a safe baseline before
// the real matrix loads and so the RBAC editor always has every key present.
// ---------------------------------------------------------------------------
export function emptyMatrix(): UserPermissionMatrix {
  return {
    projects: {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      scope: 'assigned',
      canViewOverview: false,
      canViewFinancials: false,
      canEditFinancials: false,
      canViewTeam: false,
      canEditTeam: false,
      canViewResources: false,
      canEditResources: false,
      canViewMeetingNotes: false,
      canEditMeetingNotes: false,
      canViewVault: false,
      canRevealVault: false,
      canEditVault: false,
    },
    tasks: {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canBulkEdit: false,
      canViewReports: false,
      scope: 'assigned',
    },
    leads: {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canConvert: false,
      canViewActions: false,
      canEditActions: false,
      canConfigurePipeline: false,
    },
    clients: {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canViewContacts: false,
      canEditContacts: false,
      canViewResources: false,
      canEditResources: false,
      canViewMeetingNotes: false,
      canEditMeetingNotes: false,
    },
    meetings: {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      scope: 'involved',
      canViewNotes: false,
      canEditNotes: false,
      canManageContacts: false,
    },
    videoMeetings: {
      canView: false,
      canCreate: false,
      canManage: false,
    },
    socialPosts: {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canManageShareLinks: false,
    },
    suppliers: {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
    },
    actors: {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
    },
    whatsapp: {
      canView: false,
      canConfigure: false,
    },
    subscriptions: {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
    },
    financials: {
      canView: false,
      canViewPnL: false,
      canViewInvoices: false,
      canCreateInvoices: false,
      canEditInvoices: false,
      canDeleteInvoices: false,
      canViewSmartBill: false,
      canSyncSmartBill: false,
      canAssociateSmartBill: false,
      canViewSpv: false,
      canSyncSpv: false,
      canIssueInvoices: false,
      canApproveInvoices: false,
      canViewBankStatements: false,
      canUploadBankStatements: false,
      canReconcile: false,
      canExportReports: false,
      canSendReminders: false,
      canManageScheduledPayments: false,
    },
    people: {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canViewCapacity: false,
      canEditAllocations: false,
      canViewRates: false,
      canManageRoles: false,
    },
    dailySummary: {
      canView: false,
      scope: 'self',
      canExportPdf: false,
      canSendEmail: false,
    },
    weeklySummary: {
      canView: false,
      scope: 'self',
      canExport: false,
    },
    aiAssistant: {
      canAccess: false,
      canCreateTasks: false,
      canCreateLeads: false,
      canIngestDocuments: false,
      canQueryFinancials: false,
      canWriteKb: false,
      modelAccess: 'haiku',
    },
    settings: {
      canAccessSettings: false,
      canManageCompanies: false,
      canEditIntegrations: false,
      canEditExpenseCategories: false,
      canEditLeadPipelines: false,
      canEditDepartments: false,
      canEditServices: false,
      canEditTaskStatuses: false,
      canEditTaskTemplates: false,
      canEditEmailTemplates: false,
      canManageUsers: false,
      canManageRoles: false,
    },
    leave: {
      canViewAll: false,
      canApprove: false,
      canManage: false,
    },
    dashboard: {
      canView: false,
      canViewFinancialCards: false,
      canViewTeamAllocation: false,
      canViewActivityFeed: false,
    },
  };
}

/**
 * Pure permission check, mirroring backend `can()`: returns true only when the
 * named module + action exists and is boolean `true`. Any missing / non-boolean
 * flag denies (deny by default).
 */
export function can(
  matrix: UserPermissionMatrix | null | undefined,
  module: PermissionModule,
  action: PermissionAction,
): boolean {
  if (!matrix) return false;
  const mod = matrix[module] as unknown as Record<string, unknown> | undefined;
  if (!mod) return false;
  return mod[action] === true;
}

// ---------------------------------------------------------------------------
// Role templates
// ---------------------------------------------------------------------------

export interface RoleTemplate {
  id: string;
  name: string;
  description: string | null;
  permissions: UserPermissionMatrix;
  isSystem: boolean;
  /** Stable key of a system template ('admin', 'developer', …); null for custom ones. */
  systemKey?: string | null;
  /** Accounts assigned to this template (for the delete dialog). */
  userCount?: number;
  createdAt: string;
  updatedAt: string;
}

/** The Admin template: never deleted. */
export const ADMIN_TEMPLATE_KEY = 'admin';

/** A system template an admin deleted, which can be restored. */
export interface DeletedSystemTemplate {
  key: string;
  name: string;
}

export interface UserPermissionRow {
  userId: string;
  templateId: string | null;
  permissions: UserPermissionMatrix;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** GET /api/me/permissions — the authenticated user's full resolved matrix. */
export async function getMyPermissions(): Promise<UserPermissionMatrix> {
  const res = await apiFetch<Envelope<UserPermissionMatrix>>('/api/me/permissions', {
    headers: authHeaders(),
  });
  return res.data;
}

/** GET /api/role-templates — list system + custom templates. */
export async function listRoleTemplates(): Promise<RoleTemplate[]> {
  const res = await apiFetch<Envelope<RoleTemplate[]>>('/api/role-templates', {
    headers: authHeaders(),
  });
  return res.data;
}

export interface RoleTemplateInput {
  name: string;
  description?: string | null;
  permissions: UserPermissionMatrix;
}

/** POST /api/role-templates — create a custom template (canManageRoles). */
export async function createRoleTemplate(input: RoleTemplateInput): Promise<RoleTemplate> {
  const res = await apiFetch<Envelope<RoleTemplate>>('/api/role-templates', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

/** PUT /api/role-templates/:id — update a custom template (canManageRoles). */
export async function updateRoleTemplate(
  id: string,
  input: Partial<RoleTemplateInput>,
): Promise<RoleTemplate> {
  const res = await apiFetch<Envelope<RoleTemplate>>(`/api/role-templates/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

/**
 * DELETE /api/role-templates/:id — delete a template, system ones included
 * (not Admin). Its users are moved to `replaceWith` first; the server refuses
 * (TEMPLATE_IN_USE) a template in use without one.
 */
export async function deleteRoleTemplate(id: string, replaceWith?: string): Promise<void> {
  const query = replaceWith ? `?replaceWith=${encodeURIComponent(replaceWith)}` : '';
  await apiFetch<Envelope<{ id: string; deleted: boolean; movedUserCount: number }>>(
    `/api/role-templates/${id}${query}`,
    { method: 'DELETE', headers: authHeaders() },
  );
}

/** GET /api/role-templates/system/deleted — the system templates that can be restored. */
export async function listDeletedSystemTemplates(): Promise<DeletedSystemTemplate[]> {
  const res = await apiFetch<Envelope<DeletedSystemTemplate[]>>(
    '/api/role-templates/system/deleted',
    { headers: authHeaders() },
  );
  return res.data;
}

/** POST /api/role-templates/system/:key/restore — recreate it with its default matrix. */
export async function restoreSystemTemplate(key: string): Promise<RoleTemplate> {
  const res = await apiFetch<Envelope<RoleTemplate>>(
    `/api/role-templates/system/${encodeURIComponent(key)}/restore`,
    { method: 'POST', headers: authHeaders() },
  );
  return res.data;
}

/** GET /api/users/:id/permissions — a specific user's stored matrix + template. */
export async function getUserPermissions(userId: string): Promise<UserPermissionRow> {
  const res = await apiFetch<Envelope<UserPermissionRow>>(`/api/users/${userId}/permissions`, {
    headers: authHeaders(),
  });
  return res.data;
}

/**
 * PUT /api/users/:id/permissions — replace a user's matrix and/or attach a
 * template. Send `permissions` for custom overrides, `roleTemplateId` for a
 * template, or both (custom overrides win, template stored for reference).
 */
export async function putUserPermissions(
  userId: string,
  input: { permissions?: UserPermissionMatrix | null; roleTemplateId?: string | null },
): Promise<UserPermissionRow> {
  const res = await apiFetch<Envelope<UserPermissionRow>>(`/api/users/${userId}/permissions`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

/** POST /api/users/:id/apply-template — set a user's matrix from a template. */
export async function applyTemplateToUser(
  userId: string,
  roleTemplateId: string,
): Promise<UserPermissionRow> {
  const res = await apiFetch<Envelope<UserPermissionRow>>(`/api/users/${userId}/apply-template`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ roleTemplateId }),
  });
  return res.data;
}
